import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faEye, faUser } from '@fortawesome/free-solid-svg-icons';
import BlogPostShare from './BlogPostShare';
import BlogVisitCounter from '../Common/BlogVisitCounter';
import blogVisitService from '../../services/BlogVisitService';

const BlogPostContent = ({ post }) => {
  // Track visit when component mounts
  useEffect(() => {
    if (post?.slug) {
      blogVisitService.trackVisit(post.slug);
    }
  }, [post?.slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const ContentImage = ({ src, alt }) => {
    const [loaded, setLoaded] = useState(false);
    return (
      <figure className="blog-post-content__figure">
        <div className={`blog-post-image-wrapper ${loaded ? 'is-loaded' : 'is-loading'}`}>
          <div className="blog-post-image-skeleton" />
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        </div>
      </figure>
    );
  };

  const normalizeContent = () => {
    const raw = post?.content;
    if (!raw) return [];

    // Ya es arreglo de bloques
    if (Array.isArray(raw)) return raw;

    // Intentar parsear si viene como string JSON
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object' && parsed.type && parsed.content) return [parsed];
      } catch (_) {
        // Texto plano como único bloque
        if (raw.trim()) return [{ type: 'text', content: raw.trim() }];
      }
    }
    return [];
  };

  const resolveImageUrl = (imageId, blockIndex) => {
    if (!post?.content_images || !post.content_images.length) return null;
    
    // Si imageId es válido, usarlo directamente
    if (imageId !== null && imageId !== undefined) {
      const match = post.content_images.find(img => img.id === imageId);
      if (match?.image_url) {
        return match.image_url;
      }
    }
    
    // Fallback: usar el índice del bloque solo si imageId es null
    if (imageId === null && post.content_images[blockIndex]) {
      return post.content_images[blockIndex].image_url;
    }
    
    return null;
  };

  const renderBlock = (block, index) => {
    if (!block || typeof block !== 'object') return null;
    if (block.type === 'text') {
      const text = block.content || '';
      // Respetar saltos de línea
      return (
        <div key={`text-${index}`} className="blog-post-content__paragraphs">
          {text.split(/\n+/).map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      );
    }

    if (block.type === 'image') {
      const src = resolveImageUrl(block.image_id, index);
      if (!src) return null;
      return (
        <ContentImage key={`img-${index}`} src={src} alt={block.caption || post.title} />
      );
    }

    return null;
  };

  const blocks = normalizeContent();
  const hasRenderableContent = blocks.length > 0 || (post?.plain_text_content && post.plain_text_content.trim());

  const hasImageBlocks = blocks.some(b => b && b.type === 'image');
  const hasContentImages = Array.isArray(post?.content_images) && post.content_images.length > 0;

  return (
    <article className="blog-post-content">
      {/* Header con fecha de publicación y contador de visitas */}
      <div className="blog-post-content__header">
        <div className="blog-post-content__published">
          Published: 
          <FontAwesomeIcon icon={faCalendarAlt} />
          <span>{formatDate(post.published_at)}</span>
          <BlogVisitCounter postSlug={post.slug} postData={post} className="blog-post-content__visit-counter" />
        </div>
      </div>
      
      {/* Contenido del post */}
      <div className="blog-post-content__body">
        {hasRenderableContent ? (
          blocks.length > 0 ? (
            blocks.map(renderBlock)
          ) : (
            <p>{post.plain_text_content}</p>
          )
        ) : (
          <p />
        )}

        {/* Fallback: si no hay bloques de imagen pero sí hay content_images, mostrar galería */}
        {!hasImageBlocks && hasContentImages && (
          <div className="blog-post-content__gallery">
            {post.content_images.map(img => (
              <ContentImage key={img.id} src={img.image_url} alt={img.caption || post.title} />
            ))}
          </div>
        )}
      </div>

      {/* Footer del post */}
      <div className="blog-post-content__footer">
      </div>
      
      {/* CTA Box */}
      <div className="blog-post-content__cta">
        <div className="blog-post-content__cta-content">
          <h3>Did you enjoy what you read?</h3>
          <p>If this article provided value to you, share it with friends and family who might also benefit from this information.</p>
          <div className="blog-post-content__cta-actions">
            <BlogPostShare post={post} />
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogPostContent; 